import React, { useEffect, useState, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { Button } from './button';
import { 
  Bold, 
  Italic, 
  List, 
  ListOrdered, 
  Heading2, 
  RemoveFormatting, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  AlignJustify,
  Link as LinkIcon,
  ExternalLink,
  Wand2,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from './input';
import { useDebounce } from '@/hooks/use-debounce';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  rows?: number;
  onProcessWithAI?: () => void;
  isProcessing?: boolean;
  readOnly?: boolean;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Escreva aqui...',
  className = '',
  onFocus,
  onBlur,
  rows = 4,
  onProcessWithAI,
  isProcessing = false,
  readOnly = false
}: RichTextEditorProps) {
  console.log('[DEBUG][RichTextEditor] Render - value prop:', value);
  const [linkUrl, setLinkUrl] = useState<string>('');
  const [linkOpen, setLinkOpen] = useState<boolean>(false);
  const [localContent, setLocalContent] = useState<string>(value || '');
  
  // Referência para controlar se o componente já foi inicializado
  const isInitialized = useRef(false);
  // Referência para impedir atualizações cíclicas durante a estabilização
  const isStabilizing = useRef(false);
  // Referência para armazenar o valor inicial significativo durante a montagem
  const initialValueRef = useRef<string | null>(null);
  
  // Efeito especial de inicialização que roda apenas uma vez na montagem
  useEffect(() => {
    // Detectar se temos um valor significativo no valor inicial ou em atualizações iniciais
    const significantValue = value && value.trim() !== '' && 
                           (value.includes('</') || value.length > 10);
    
    // Apenas na primeira renderização
    if (!isInitialized.current) {
      // Logging removed
      
      // Se temos um valor significativo, armazená-lo como referência
      if (significantValue) {
        initialValueRef.current = value;
        setLocalContent(value);
      }
      
      isInitialized.current = true;
    }
    // Este efeito roda apenas uma vez na montagem
  }, []);
  
  // Debounce the content changes to avoid excessive updates
  const debouncedContent = useDebounce<string>(localContent, 300);

  // Effect to call onChange only when debouncedContent changes
  useEffect(() => {
    console.log('[DEBUG][RichTextEditor] localContent mudou:', localContent);
    // Se estamos no processo de estabilização, ignorar este ciclo
    if (isStabilizing.current) {
      return;
    }
    
    // Se não estamos inicializados ainda, não propagamos mudanças para cima
    if (!isInitialized.current) {
      return;
    }
    
    // Se temos um valor inicial significativo definido e o conteúdo local está vazio,
    // restaurar para o valor inicial
    if (initialValueRef.current && (!localContent || localContent.trim() === '')) {
      // Logging removed
      
      // Impedir ciclos
      isStabilizing.current = true;
      
      // Restaurar para o valor inicial em vez de propagar o vazio
      setLocalContent(initialValueRef.current);
      
      // Agendar o fim da estabilização
      setTimeout(() => {
        isStabilizing.current = false;
      }, 50);
      
      return;
    }
    
    // Se o conteúdo local mudou em relação ao valor externo e não estamos restaurando
    if (debouncedContent !== value && !isStabilizing.current) {
      // Logging removed
      onChange(debouncedContent);
      console.log('[DEBUG][RichTextEditor] onChange chamado - debouncedContent:', debouncedContent);
    }
  }, [debouncedContent, onChange, value, localContent]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        defaultAlignment: 'left',
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-voah-teal underline decoration-voah-teal hover:text-voah-teal/80',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      // Update local content immediately for responsive typing
      const newContent = editor.getHTML();
      
      // Se temos conteúdo significativo, atualizar a referência inicial
      // Isso ajuda a garantir que tenhamos sempre uma boa referência para restaurar
      if (newContent && newContent.trim() !== '' && 
         (newContent.includes('</') || newContent.length > 10) &&
         !initialValueRef.current) {
        initialValueRef.current = newContent;
        // Logging removed
      }
      
      setLocalContent(newContent);
    },
    onFocus: () => {
      onFocus && onFocus();
    },
    onBlur: () => {
      // When focus is lost, ensure we've pushed any final changes
      if (localContent !== value) {
        onChange(localContent);
      }
      onBlur && onBlur();
    },
    editable: !readOnly,
  });

  // Atualizar o conteúdo do editor quando o valor mudar externamente
  useEffect(() => {
    // Apenas atualizamos o editor com dados externos se:
    // 1. O editor está definido
    // 2. O conteúdo é diferente
    // 3. Não estamos no meio de uma estabilização
    if (!editor || !isInitialized.current) return;
    
    // Se recebemos uma string vazia e já temos conteúdo significativo inicial,
    // não limpar o editor para evitar perda de dados
    if ((!value || value.trim() === '') && initialValueRef.current) {
      // Logging removed
      return;
    }
    
    // Se o conteúdo do editor é diferente do valor recebido, atualizar
    const currentContent = editor.getHTML();
    if (value !== currentContent && !isStabilizing.current) {
      // Logging removed
      
      // Preservar a seleção atual, se houver
      const hasSelection = editor.state.selection.anchor !== editor.state.selection.head;
      const from = editor.state.selection.from;
      const to = editor.state.selection.to;
      
      // Atualizar conteúdo
      editor.commands.setContent(value);
      
      // Restaurar seleção, se havia uma
      if (hasSelection) {
        try {
          editor.commands.setTextSelection({
            from,
            to
          });
        } catch (error) {
          // Error logging left for debugging purposes
          console.error('[RichTextEditor] Error restoring selection:', error);
        }
      }
    }
  }, [editor, value]);

  // Função para adicionar ou atualizar um link
  const setLink = () => {
    if (!editor) return;
    
    // Cancelar se não houver URL
    if (!linkUrl) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // Verificar se a URL tem o protocolo, se não, adicionar https://
    const url = linkUrl.trim();
    const urlWithProtocol = url.startsWith('http://') || url.startsWith('https://') 
      ? url 
      : `https://${url}`;

    // Definir o link
    editor.chain().focus().extendMarkRange('link').setLink({ href: urlWithProtocol }).run();
    
    // Fechar o popover e limpar o campo
    setLinkOpen(false);
    setLinkUrl('');
  };

  // Verificar se há um link selecionado ao abrir o popover
  const handleLinkPopoverOpenChange = (open: boolean) => {
    setLinkOpen(open);
    
    if (open && editor) {
      const linkMark = editor.getAttributes('link');
      if (linkMark.href) {
        setLinkUrl(linkMark.href);
      } else {
        setLinkUrl('');
      }
    }
  };

  // Calcular a altura com base no número de linhas
  const editorHeight = `${rows * 1.5}rem`;

  return (
    <div className={`${readOnly ? '' : 'border rounded-md'} ${className} font-sora`}>
      {!readOnly && (
        <div className="flex flex-wrap gap-1 p-1 border-b bg-gray-50">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 w-8 p-0",
              editor?.isActive('bold') && "bg-gray-200"
            )}
            onClick={() => editor?.chain().focus().toggleBold().run()}
            disabled={!editor?.can().chain().focus().toggleBold().run()}
            title="Negrito"
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 w-8 p-0",
              editor?.isActive('italic') && "bg-gray-200"
            )}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            disabled={!editor?.can().chain().focus().toggleItalic().run()}
            title="Itálico"
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 w-8 p-0",
              editor?.isActive('heading', { level: 2 }) && "bg-gray-200"
            )}
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            disabled={!editor?.can().chain().focus().toggleHeading({ level: 2 }).run()}
            title="Título"
          >
            <Heading2 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 w-8 p-0",
              editor?.isActive('bulletList') && "bg-gray-200"
            )}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            disabled={!editor?.can().chain().focus().toggleBulletList().run()}
            title="Lista com marcadores"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 w-8 p-0",
              editor?.isActive('orderedList') && "bg-gray-200"
            )}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            disabled={!editor?.can().chain().focus().toggleOrderedList().run()}
            title="Lista numerada"
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
          
          {/* Link Button */}
          <Popover open={linkOpen} onOpenChange={handleLinkPopoverOpenChange}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  "h-8 w-8 p-0",
                  editor?.isActive('link') && "bg-gray-200"
                )}
                title="Adicionar link"
              >
                <LinkIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">URL do link</label>
                <div className="flex gap-2">
                  <Input
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://exemplo.com"
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setLink();
                      }
                    }}
                  />
                  <Button 
                    type="button" 
                    size="sm"
                    onClick={setLink}
                    className="bg-voah-teal hover:bg-voah-teal/90"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    {editor?.isActive('link') ? 'Atualizar' : 'Adicionar'}
                  </Button>
                </div>
                {editor?.isActive('link') && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      editor.chain().focus().unsetLink().run();
                      setLinkOpen(false);
                    }}
                    className="mt-2 text-red-500 hover:text-white hover:bg-red-500"
                  >
                    Remover link
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Separador */}
          <div className="w-px h-8 bg-gray-300 mx-1"></div>
          
          {/* Botões de alinhamento */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 w-8 p-0",
              editor?.isActive({ textAlign: 'left' }) && "bg-gray-200"
            )}
            onClick={() => editor?.chain().focus().setTextAlign('left').run()}
            title="Alinhar à esquerda"
          >
            <AlignLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 w-8 p-0",
              editor?.isActive({ textAlign: 'center' }) && "bg-gray-200"
            )}
            onClick={() => editor?.chain().focus().setTextAlign('center').run()}
            title="Centralizar"
          >
            <AlignCenter className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 w-8 p-0",
              editor?.isActive({ textAlign: 'right' }) && "bg-gray-200"
            )}
            onClick={() => editor?.chain().focus().setTextAlign('right').run()}
            title="Alinhar à direita"
          >
            <AlignRight className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 w-8 p-0",
              editor?.isActive({ textAlign: 'justify' }) && "bg-gray-200"
            )}
            onClick={() => editor?.chain().focus().setTextAlign('justify').run()}
            title="Justificar"
          >
            <AlignJustify className="h-4 w-4" />
          </Button>
          
          {/* Separador */}
          <div className="w-px h-8 bg-gray-300 mx-1"></div>
          
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().run()}
            disabled={!editor?.can().chain().focus().unsetAllMarks().run()}
            title="Limpar formatação"
          >
            <RemoveFormatting className="h-4 w-4" />
          </Button>
          
          {/* Botão de Processar com IA (apenas se a função de callback for fornecida) */}
          {onProcessWithAI && (
            <>
              {/* Separador */}
              <div className="w-px h-8 bg-gray-300 mx-1"></div>
              
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  "h-8 w-8 p-0 text-voah-teal hover:text-white hover:bg-voah-teal"
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onProcessWithAI();
                }}
                disabled={isProcessing || !editor?.getText().trim()}
                title="Processar texto com IA"
              >
                {isProcessing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Wand2 className="h-4 w-4" />
                )}
              </Button>
            </>
          )}
        </div>
      )}
      <EditorContent 
        editor={editor} 
        className={`prose prose-sm max-w-none ${readOnly ? 'p-0' : 'p-3'} focus:outline-none font-sora`} 
        style={{ 
          minHeight: readOnly ? 'auto' : editorHeight, 
          maxHeight: readOnly ? 'none' : '30rem', 
          overflowY: readOnly ? 'visible' : 'auto',
          fontFamily: "'Sora', sans-serif"
        }}
      />
    </div>
  );
} 